import * as React from "react";
import { connect } from "react-redux";
import get from "lodash/get";
import set from "lodash/set";
import isEmpty from "lodash/isEmpty";
import { StoreState } from "../../reducers";
import {
  LoyaltyQuestButton as LoyaltyQuestButtonComponent,
  LoyaltyQuestButtonComponentProps,
} from "../../components/LoyaltyQuestButton";
import { getLoyaltyQuestToken } from "../../actions/loyalty/getLoyaltyQuestToken";
import {
  getIsLoadingLoyaltyQuestToken,
  getLoyaltyQuestToken as getLoyaltyQuestTokenSelector,
} from "../../reducers/loyalty/methods";
import { withAction } from "../Actions/withActions";

interface LoyaltyQuestButtonContainerProps extends Omit<
  LoyaltyQuestButtonComponentProps,
  "campaignId" | "customerId"
> {
  getLoyaltyQuestToken: typeof getLoyaltyQuestToken;
  isLoading: ReturnType<typeof getIsLoadingLoyaltyQuestToken>;
  loyaltyQuestToken: ReturnType<typeof getLoyaltyQuestTokenSelector>;
}

/**
 * Container component for LQButton that handles LoyaltyQuest-specific logic.
 * Dispatches remote requests when actionURL contains loyaltyquest merge variables.
 */
class LoyaltyQuestButton extends React.Component<LoyaltyQuestButtonContainerProps> {
  componentDidMount() {
    const { isLoading, action, loyaltyQuestToken } = this.props;
    const actionURL = get(action, "args.url", "");

    // Only make the request if:
    // 1. We're not already loading
    // 2. We don't have data yet
    // 3. The actionURL contains LoyaltyQuest merge variables
    if (
      !isLoading &&
      isEmpty(loyaltyQuestToken) &&
      this.hasLoyaltyQuestVariables(actionURL)
    ) {
      this.props.getLoyaltyQuestToken();
    }
  }

  componentDidUpdate(prevProps: LoyaltyQuestButtonContainerProps) {
    const { isLoading, action } = this.props;
    const actionURL = get(action, "args.url", "");
    const prevActionURL = get(prevProps.action, "args.url", "");

    // Re-fetch data if actionURL changes and contains LoyaltyQuest variables
    if (
      actionURL &&
      actionURL !== prevActionURL &&
      this.hasLoyaltyQuestVariables(actionURL) &&
      !isLoading
    ) {
      this.props.getLoyaltyQuestToken();

      return;
    }

    // Replace action URL with the loyalty quest token
    if (this.props.loyaltyQuestToken && actionURL) {
      const newActionURL = actionURL
        .replace("{{loyaltyquest_jwt}}", this.props.loyaltyQuestToken.token)
        .replace(
          "{{loyaltyquest_client_id}}",
          this.props.loyaltyQuestToken.clientId
        );
      set(this.props, "action.args.url", newActionURL);
    }
  }

  render() {
    const { loyaltyQuestToken, isLoading, clientId, ...rest } = this.props;

    return (
      <LoyaltyQuestButtonComponent
        {...rest}
        isLoading={isLoading}
        loyaltyQuestToken={loyaltyQuestToken}
        clientId={clientId}
      />
    );
  }

  /**
   * Checks if the actionURL contains LoyaltyQuest merge variables
   */
  private hasLoyaltyQuestVariables = (url: string): boolean => {
    return (
      url.includes("{{loyaltyquest_jwt}}") &&
      url.includes("{{loyaltyquest_client_id}}")
    );
  };
}

const mapStateToProps = (state: StoreState) => ({
  loyaltyQuestToken: getLoyaltyQuestTokenSelector(state.loyalty),
  isLoading: getIsLoadingLoyaltyQuestToken(state.loyalty),
});

export default withAction(
  connect(mapStateToProps, { getLoyaltyQuestToken })(LoyaltyQuestButton)
);
